import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message } from 'antd';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { AdminAuthProvider } from '@/context/AdminAuthContext';
import type { AdminUser } from '@/services/admin-auth';
import { getAdminUser } from '@/services/admin-auth';
import { checkAdminAuth, isAdminRoute } from '@/utils/adminAuthGuard';
import { isSuperAdmin } from '@/utils/adminDeleteGuard';
import { filterAdminMenuData, hasAdminPageAccess } from '@/utils/adminPermissions';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

const toCurrentUser = (adminUser: AdminUser): API.CurrentUser => ({
  id: Number(adminUser.adminId || 0),
  name: adminUser.realName || adminUser.username,
  avatar: '/images/default-admin-avatar.png',
  email: undefined,
  signature: undefined,
  title: undefined,
  group: undefined,
  notifyCount: 0,
  unreadCount: 0,
  country: undefined,
  access: 'admin',
  geographic: undefined,
  address: undefined,
  phone: undefined,
});

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    const adminUser = getAdminUser();
    if (adminUser) {
      return toCurrentUser(adminUser);
    }
    history.push(loginPath);
    return undefined;
  };

  // 如果不是登录页面，执行
  const { location } = history;
  const isAdminPath = isAdminRoute(location.pathname);

  // 对管理员路由执行管理员用户信息查询
  if (
    isAdminPath &&
    !['/admin/login', '/admin/register'].includes(location.pathname)
  ) {
    const adminUser = getAdminUser();
    if (adminUser) {
      return {
        fetchUserInfo,
        currentUser: toCurrentUser(adminUser),
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      const isAdminPath = isAdminRoute(location.pathname);
      // 检查管理员认证
      checkAdminAuth(location.pathname);
      if (isAdminPath && !hasAdminPageAccess(location.pathname)) {
        const fallbackPaths = [
          '/dashboard/analysis',
          '/admin/user-management',
          '/transaction/order-management',
          '/permission/admin-accounts',
        ];
        const nextPath = fallbackPaths.find((path) => hasAdminPageAccess(path));
        message.error('当前账号无权限访问该页面');
        history.push(nextPath && nextPath !== location.pathname ? nextPath : loginPath);
        return;
      }
      // 只对非管理员路由检查用户认证
      if (
        !isAdminPath &&
        !initialState?.currentUser &&
        location.pathname !== loginPath
      ) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    menuDataRender: (menuData) => filterAdminMenuData(menuData),
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <AdminAuthProvider>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </AdminAuthProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: '/',
  ...errorConfig,
  // 合并拦截器：先执行errorConfig中的拦截器，再执行这里的拦截器
  requestInterceptors: [
    // errorConfig中的拦截器会通过展开被继承，但需要显式重新包含
    ...(errorConfig.requestInterceptors || []),
    // 为管理员路由的请求添加 Admin-Id 和 Admin-Name 请求头
    (url, options) => {
      if (url.startsWith('/api/') && isAdminRoute(window.location.pathname)) {
        const method = (options.method || 'GET').toUpperCase();
        const deleteEndpointPattern = /\/(?:batch-)?delete(?:\/|$)/i;
        const isDeleteOperation =
          method === 'DELETE' || deleteEndpointPattern.test(url);
        if (isDeleteOperation && !isSuperAdmin()) {
          message.error('仅超级管理员可执行删除操作');
          throw new Error('DELETE_OPERATION_FORBIDDEN');
        }

        const adminUser = getAdminUser();
        if (adminUser?.adminId) {
          options.headers = {
            ...options.headers,
            'Admin-Id': String(adminUser.adminId),
            'Admin-Name': adminUser.username || adminUser.realName || '',
          };
        }
      }
      return { url, options };
    },
  ],
};
